import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
  Badge,
  Container,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import { toast } from "react-toastify";
import { FiDownload, FiRefreshCw } from "react-icons/fi";
import { format } from "date-fns";
import DebounceSmallSearchInput from "../Constant/DebounceSmallSearchInput";
import GlobalSearchableDropdown from "../Constant/SearchableDropdown";
import Reportselect from "./Reportselect";
import Pagination from "../Constant/Pagination";
import { getAssetMaintenanceReport } from "../../services/ReportServices";
import "react-datepicker/dist/react-datepicker.css";
import { BiSolidReport } from "react-icons/bi";

const periodOptions = [
  { label: "All", value: "all" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 Days", value: "7_days" },
  { label: "Last 15 Days", value: "15_days" },
  { label: "Last 30 Days", value: "30_days" },
  { label: "Last Month", value: "last_month" },
  { label: "Last 3 Months", value: "3_months" },
  { label: "Custom", value: "custom" },
];

const maintenanceTypeOptions = [
  { value: "", label: "All Types" },
  { value: "PREVENTIVE", label: "Preventive" },
  { value: "CORRECTIVE", label: "Corrective" },
];

const AssetMaintenanceReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(periodOptions[4]); // Default to "This Month"

  const [filters, setFilters] = useState({
    type: "",
    asset_number: "",
    search: "",
    period: "monthly",
    start_date: "",
    end_date: "",
    page: 1,
    page_size: 10,
  });

  useEffect(() => {
    const today = new Date();
    let start_date = "";
    let end_date = format(today, "yyyy-MM-dd");

    switch (selectedPeriod?.value) {
      case "all":
        start_date = "";
        end_date = "";
        break;
      case "yesterday":
        start_date = format(
          new Date(today.setDate(today.getDate() - 1)),
          "yyyy-MM-dd"
        );
        end_date = start_date;
        break;
      case "7_days":
        start_date = format(
          new Date(today.setDate(today.getDate() - 6)),
          "yyyy-MM-dd"
        );
        end_date = format(new Date(), "yyyy-MM-dd");
        break;
      case "15_days":
        start_date = format(
          new Date(today.setDate(today.getDate() - 14)),
          "yyyy-MM-dd"
        );
        end_date = format(new Date(), "yyyy-MM-dd");
        break;
      case "30_days":
        start_date = format(
          new Date(today.setDate(today.getDate() - 29)),
          "yyyy-MM-dd"
        );
        end_date = format(new Date(), "yyyy-MM-dd");
        break;
      case "last_month": {
        const firstDayThisMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          1
        );
        const lastDayLastMonth = new Date(firstDayThisMonth - 1);
        start_date = format(
          new Date(
            lastDayLastMonth.getFullYear(),
            lastDayLastMonth.getMonth(),
            1
          ),
          "yyyy-MM-dd"
        );
        end_date = format(lastDayLastMonth, "yyyy-MM-dd");
        break;
      }
      case "3_months":
        start_date = format(
          new Date(today.getFullYear(), today.getMonth() - 2, 1),
          "yyyy-MM-dd"
        );
        end_date = format(new Date(), "yyyy-MM-dd");
        break;
      case "weekly":
        start_date = format(
          new Date(today.setDate(today.getDate() - today.getDay())),
          "yyyy-MM-dd"
        );
        end_date = format(new Date(), "yyyy-MM-dd");
        break;
      case "monthly":
        start_date = format(
          new Date(today.getFullYear(), today.getMonth(), 1),
          "yyyy-MM-dd"
        );
        end_date = format(new Date(), "yyyy-MM-dd");
        break;
      case "yearly":
        start_date = format(new Date(today.getFullYear(), 0, 1), "yyyy-MM-dd");
        end_date = format(new Date(), "yyyy-MM-dd");
        break;
      case "custom":
        start_date = filters.start_date;
        end_date = filters.end_date;
        break;
      default:
        break;
    }

    setFilters((prev) => ({
      ...prev,
      period: selectedPeriod?.value,
      start_date,
      end_date,
      page: 1,
    }));
  }, [selectedPeriod]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value, page: 1 }));
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (
        filters.period === "custom" &&
        (!filters.start_date || !filters.end_date)
      ) {
        setReportData([]);
        setTotalCount(0);
        setSummary(null);
        return;
      }

      const params = {
        type: filters.type || undefined,
        asset_number: filters.asset_number || undefined,
        search: filters.search || undefined,
        period: filters.period,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        page: filters.page,
        page_size: filters.page_size,
      };

      const response = await getAssetMaintenanceReport(params);
      if (response?.data?.results) {
        setReportData(response.data.results);
        setTotalCount(response.data.count || 0);
        setSummary(response.data.summary || null);
      } else {
        throw new Error("Unexpected response format");
      }
    } catch (err) {
      console.error(err);
      const message =
        err.response?.data?.error || err.message || "Failed to load report";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const exportParams = {
        ...filters,
        export: true,
      };

      const response = await getAssetMaintenanceReport(exportParams, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "maintenance_report.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Export started successfully");
    } catch (error) {
      toast.error("Export failed. Please try again.");
      console.error(error);
    } finally {
      setExportLoading(false);
    }
  };

  const handlePageChange = (selectedPage) => {
    setFilters((prev) => ({ ...prev, page: selectedPage.selected + 1 }));
    window.scrollTo(0, 0);
  };

  const getTypeBadge = (type) => {
    const typeColors = {
      PREVENTIVE: "primary",
      CORRECTIVE: "warning",
    };
    return (
      <Badge bg={typeColors[type] || "secondary"}>
        {maintenanceTypeOptions.find((opt) => opt.value === type)?.label ||
          type}
      </Badge>
    );
  };

  useEffect(() => {
    const delay = setTimeout(fetchData, 300);
    return () => clearTimeout(delay);
  }, [
    filters.page,
    filters.page_size,
    filters.asset_number,
    filters.type,
    filters.search,
    filters.start_date,
    filters.end_date,
  ]);

  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="text-primary mb-0">Asset Maintenance Report</h2>
          <ol className="breadcrumb breadcrumb-arrow mb-0">
            <li className="breadcrumb-item">
              <Link to="/">Dashboard</Link>
            </li>
            <li className="breadcrumb-item active">Maintenance Report</li>
          </ol>
        </div>
        <div
          className="d-flex align-items-center gap-2 ms-auto mt-2 mt-md-0"
          style={{ zIndex: 10 }}
        >
          <div style={{ width: "200px" }}>
            <Reportselect />
          </div>
          <Button
            variant="success"
            onClick={handleExport}
            disabled={exportLoading || loading}
            style={{ height: "41px" }}
          >
            {exportLoading ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <FiDownload className="me-1" />
            )}
            Export
          </Button>
        </div>
      </div>
      <Card style={{ minHeight: "350px" }}>
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2 text-primary fw-bold fs-5 mb-0">
            <BiSolidReport size={22} color="#255d5c" />
            <h5 className="mb-0">Asset Maintenance Report</h5>
          </div>

          {summary && (
            <div className="d-flex gap-3">
              <div className="text-center">
                <div className=" small">Total Maintenances</div>
                <div className="fw-bold">{summary.total_maintenances}</div>
              </div>
              <div className="text-center">
                <div className=" small">Total Cost</div>
                <div className="fw-bold">
                  ₹{summary.total_cost?.toFixed(2) || 0}
                </div>
              </div>
              <div className="text-center">
                <div className=" small">Avg. Cost</div>
                <div className="fw-bold">
                  ₹{summary.average_cost?.toFixed(2) || 0}
                </div>
              </div>
            </div>
          )}

          <div className="d-flex gap-2 flex-wrap align-items-center">
            <DebounceSmallSearchInput
              value={filters.search}
              handleSearchChange={(value) =>
                handleFilterChange("search", value)
              }
              placeholder="Search assets, description..."
              debounceTime={500}
            />

            <GlobalSearchableDropdown
              options={maintenanceTypeOptions}
              selected={maintenanceTypeOptions.find(
                (opt) => opt.value === filters.type
              )}
              onSelect={(selected) =>
                handleFilterChange("type", selected?.value || "")
              }
              placeholder="Maintenance Type"
              width="150px"
            />

            <GlobalSearchableDropdown
              options={periodOptions}
              selected={selectedPeriod}
              onSelect={setSelectedPeriod}
              placeholder="Period"
              width="150px"
            />

            {selectedPeriod?.value === "custom" && (
              <div style={{ width: "260px" }}>
                <DatePicker
                  selectsRange
                  startDate={
                    filters.start_date ? new Date(filters.start_date) : null
                  }
                  endDate={filters.end_date ? new Date(filters.end_date) : null}
                  onChange={([start, end]) => {
                    if (
                      start &&
                      end &&
                      end - start > 365 * 5 * 24 * 60 * 60 * 1000
                    ) {
                      toast.error("Date range cannot exceed 5 years");
                      return;
                    }
                    setFilters((prev) => ({
                      ...prev,
                      start_date: start ? format(start, "yyyy-MM-dd") : "",
                      end_date: end ? format(end, "yyyy-MM-dd") : "",
                      period: "custom",
                      page: 1,
                    }));
                  }}
                  isClearable
                  placeholderText="Select date range"
                  className="form-control form-control-sm"
                  dateFormat="yyyy-MM-dd"
                  minDate={new Date(2000, 0, 1)}
                  maxDate={new Date()}
                />
              </div>
            )}
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          {error && (
            <Alert variant="danger">
              {error}
              <Button
                variant="link"
                size="sm"
                onClick={fetchData}
                className="p-0 ms-2"
              >
                <FiRefreshCw /> Try again
              </Button>
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2">Loading report data...</p>
            </div>
          ) : (
            <Row>
              <Col lg={12}>
                <Card className="h-100">
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <Table size="sm" className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Asset</th>
                            <th>Type</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Duration</th>
                            <th>Cost</th>
                            <th>Description</th>
                            <th>Performed By</th>
                            <th>Location</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.length === 0 ? (
                            <tr>
                              <td colSpan="9" className="text-center py-4">
                                No data available
                              </td>
                            </tr>
                          ) : (
                            reportData.map((item, index) => (
                              <tr key={`${item.asset_number}-${index}`}>
                                <td>
                                  <div className="fw-semibold">
                                    {item.asset_number}
                                  </div>
                                  <div className=" small">
                                    {item.asset_name}
                                  </div>
                                </td>
                                <td>{getTypeBadge(item.maintenance_type)}</td>
                                <td>{item.start_date || "-"}</td>
                                <td>{item.end_date || "-"}</td>
                                <td>
                                  {item.duration
                                    ? `${item.duration} days`
                                    : "Ongoing"}
                                </td>
                                <td>₹{item.cost || "0.00"}</td>
                                <td>{item.description || "-"}</td>
                                <td>{item.performed_by || "-"}</td>
                                <td>{item.location || "-"}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                    {totalCount > 0 && (
                      <div className="d-flex justify-content-between align-items-center mt-3 px-3">
                        <div>
                          Showing {(filters.page - 1) * filters.page_size + 1}{" "}
                          to{" "}
                          {Math.min(
                            filters.page * filters.page_size,
                            totalCount
                          )}{" "}
                          of {totalCount} entries
                        </div>
                        <Pagination
                          currentPage={filters.page - 0}
                          recordsPage={filters.page_size}
                          dataLength={totalCount}
                          handlePagination={handlePageChange}
                          showPageInfo={false}
                        />
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AssetMaintenanceReport;
